import React from "react";

interface ModeRendererProps {
  brand: string;
  today: string;
  Copy: string;
}

export function VasarelyDassaultsystemesMode({ brand }: ModeRendererProps) {
  const width = 794;
  const height = 1123;

  // Dassault Systèmes gradient - blue digital twin aesthetic
  const bgGradient = (
    <defs>
      <linearGradient id="dassaultGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#001F3F", stopOpacity: 1 }} />
        <stop offset="50%" style={{ stopColor: "#0066CC", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#00BFFF", stopOpacity: 1 }} />
      </linearGradient>
    </defs>
  );

  // 3D grids - isometric projection lattice
  const grid3D: JSX.Element[] = [];
  const gridLayers = 12;
  const gridSize = 14;
  const cellSize = 50;
  
  for (let layer = 0; layer < gridLayers; layer++) {
    const zOffset = layer * 35;
    const scale = 1 - layer * 0.04;
    const opacity = 0.35 - layer * 0.02;
    
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const x = (-gridSize / 2 + i) * cellSize * scale + zOffset * 0.5;
        const y = (-gridSize / 2 + j) * cellSize * scale + zOffset * 0.5;
        
        grid3D.push(
          <rect
            key={`grid-${layer}-${i}-${j}`}
            x={x}
            y={y}
            width={cellSize * scale}
            height={cellSize * scale}
            fill="none"
            stroke="#00BFFF"
            strokeWidth={1}
            opacity={opacity}
          />
        );
      }
    }
  }

  // Parametric lattices - mathematical curve networks
  const parametricLattices: JSX.Element[] = [];
  for (let curve = 0; curve < 16; curve++) {
    const points: string[] = [];
    const offset = (curve / 16) * 2 * Math.PI;
    
    for (let t = 0; t <= 50; t++) {
      const param = t / 50;
      const angle = param * Math.PI * 4 + offset;
      const r = 120 + param * 240;
      const x = Math.cos(angle) * r;
      const y = Math.sin(angle) * r;
      points.push(`${x},${y}`);
    }
    
    parametricLattices.push(
      <polyline
        key={`parametric-${curve}`}
        points={points.join(" ")}
        fill="none"
        stroke="#0066CC"
        strokeWidth={1.5}
        opacity={0.2}
      />
    );
  }

  // Data meshes - interconnected simulation nodes
  const dataMeshes: JSX.Element[] = [];
  const meshNodes: [number, number][] = [];
  const meshRings = 5;
  
  // Generate node positions
  for (let ring = 1; ring <= meshRings; ring++) {
    const nodeCount = ring * 6;
    const radius = ring * 70;
    for (let n = 0; n < nodeCount; n++) {
      const angle = (n / nodeCount) * 2 * Math.PI;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      meshNodes.push([x, y]);
    }
  }
  
  // Connect nearby nodes
  meshNodes.forEach(([x1, y1], i) => {
    meshNodes.forEach(([x2, y2], j) => {
      if (i < j) {
        const dist = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        if (dist < 100) {
          dataMeshes.push(
            <line
              key={`mesh-${i}-${j}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#00BFFF"
              strokeWidth={1}
              opacity={0.15}
            />
          );
        }
      }
    });
  });

  // Digital twin nodes
  const twinNodes: JSX.Element[] = [];
  meshNodes.forEach(([nx, ny], i) => {
    twinNodes.push(
      <g key={`twin-${i}`}>
        <circle
          cx={nx}
          cy={ny}
          r={8}
          fill="#0066CC"
          opacity={0.6}
        />
        <circle
          cx={nx}
          cy={ny}
          r={4}
          fill="#00BFFF"
          opacity={0.8}
        />
      </g>
    );
  });

  // Simulation waves - lifecycle visualization
  const simulationWaves: JSX.Element[] = [];
  for (let wave = 0; wave < 10; wave++) {
    const radius = 80 + wave * 35;
    const segments = 24;
    
    for (let s = 0; s < segments; s++) {
      const angle1 = (s / segments) * 2 * Math.PI;
      const angle2 = ((s + 0.5) / segments) * 2 * Math.PI;
      
      const x1 = Math.cos(angle1) * radius;
      const y1 = Math.sin(angle1) * radius;
      const x2 = Math.cos(angle2) * radius;
      const y2 = Math.sin(angle2) * radius;
      
      const midAngle = (angle1 + angle2) / 2;
      const midR = radius * 1.1;
      const mx = Math.cos(midAngle) * midR;
      const my = Math.sin(midAngle) * midR;
      
      simulationWaves.push(
        <path
          key={`wave-${wave}-${s}`}
          d={`M ${x1},${y1} Q ${mx},${my} ${x2},${y2}`}
          fill="none"
          stroke="#0066CC"
          strokeWidth={1.5}
          opacity={0.2}
        />
      );
    }
  }

  // Product lifecycle circles - concentric development stages
  const lifecycleCircles: JSX.Element[] = [];
  for (let stage = 1; stage <= 14; stage++) {
    const radius = stage * 28;
    lifecycleCircles.push(
      <circle
        key={`lifecycle-${stage}`}
        cx={0}
        cy={0}
        r={radius}
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={stage % 3 === 0 ? 2.5 : 1.5}
        opacity={0.3 - stage * 0.015}
      />
    );
  }

  // Central 3DEXPERIENCE compass
  const experienceCompass = (
    <g opacity={0.4}>
      <circle cx={0} cy={0} r={55} fill="none" stroke="#FFFFFF" strokeWidth={3} />
      <circle cx={0} cy={0} r={40} fill="none" stroke="#00BFFF" strokeWidth={2.5} />
      <line x1={-50} y1={0} x2={50} y2={0} stroke="#0066CC" strokeWidth={2} />
      <line x1={0} y1={-50} x2={0} y2={50} stroke="#0066CC" strokeWidth={2} />
      <circle cx={0} cy={0} r={20} fill="#00BFFF" opacity={0.3} />
    </g>
  );

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      {bgGradient}
      <rect width={width} height={height} fill="url(#dassaultGradient)" />
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        {grid3D}
        {parametricLattices}
        {dataMeshes}
        {lifecycleCircles}
        {simulationWaves}
        {twinNodes}
        {experienceCompass}
      </g>
    </svg>
  );
}
